import React, { useContext, useState } from 'react';
import { ActionModal } from '../../../../components/Modal';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { CompanyConnection } from '../../../../../types/companyConnection/companyConnection';
import { CompanyContext } from '../../../../../context/CompanyContext';
import useMutation from '../../../../../hooks/useMutation';
import {
  AcceptCompanyConnectionResponse,
  AcceptCompanyConnectionVariables,
  CompanyConnectionMutations,
} from '../../../../../graphql/companyConnection.graphql';
import { CompanyConnectionContext } from '../../../../../context/CompanyConnectionContext';
import { Button, ButtonVariant, DeleteButton } from '../../../../components/Button';

interface PendingCompanyConnectionModalProps {
  connection: CompanyConnection;

  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function PendingCompanyConnectionModal({
  connection,
  open,
  setOpen,
}: PendingCompanyConnectionModalProps) {
  const { currentCompany, companies } = useContext(CompanyContext);
  const { companyConnections, setCompanyConnections } = useContext(CompanyConnectionContext);

  const [item, setItem] = useState<CompanyConnection>(
    new CompanyConnection(connection) ||
      new CompanyConnection({ companyId: currentCompany.id, parentId: currentCompany.id }),
  );

  const [accept, { loading: acceptLoading }] = useMutation<
    AcceptCompanyConnectionResponse,
    AcceptCompanyConnectionVariables
  >(CompanyConnectionMutations.accept, {
    onCompleted: response => {
      const acceptedConnections = new Map(companyConnections);
      response.acceptCompanyConnection.forEach(c => {
        acceptedConnections.delete(c.id);
      });
      setCompanyConnections(acceptedConnections);
      setOpen(false);
    },
  });

  const [decline, { loading: declineLoading }] = useMutation<
    AcceptCompanyConnectionResponse,
    AcceptCompanyConnectionVariables
  >(CompanyConnectionMutations.accept, {
    onCompleted: response => {
      const acceptedConnections = new Map(companyConnections);
      response.acceptCompanyConnection.forEach(c => {
        acceptedConnections.delete(c.id);
      });
      setCompanyConnections(acceptedConnections);
      setOpen(false);
    },
  });

  const handleAccept = async () => {
    await accept({ variables: { id: item.id } });
  };

  const handleDecline = async () => {
    await decline({ variables: { id: item.id } });
  };

  const loading = acceptLoading || declineLoading;

  const footer = (
    <>
      <div className='grid grid-cols-2'>
        <div className='flex gap-2 align-middle'>
          <Button
            disabled={loading}
            text={t(TranslationKey.cancel).singular.label}
            variant={ButtonVariant.secondary}
            onClick={() => setOpen(false)}
          />
          <DeleteButton
            loading={declineLoading}
            disabled={acceptLoading}
            text={t(TranslationKey.decline).singular.label}
            variant={ButtonVariant.secondary}
            onClick={handleDecline}
          />
        </div>
        <div className='flex justify-end'>
          <Button
            loading={acceptLoading}
            disabled={declineLoading}
            text={t(TranslationKey.accept).singular.label}
            onClick={handleAccept}
          />
        </div>
      </div>
    </>
  );

  return (
    <ActionModal open={open} setOpen={setOpen} title={t(TranslationKey.linkCompany).plural.upper} footer={footer}>
      <div>
        <p className='text-subtitle'>{`${companies.get(item.parentId)?.name} has requested access to your company`}</p>
      </div>
    </ActionModal>
  );
}
